import React from 'react';
import { Briefcase, Sparkles, User, Building2, Calendar, Tag, Clock } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

/**
 * View displayed when no case is selected in AI Workspace
 * Shows search, recent cases, and full case list with detailed info
 */

// Helper to get display title
const getCaseTitle = (caseItem: any) => {
  if (caseItem.title && caseItem.title !== 'General Legal Matter') {
    return caseItem.title;
  }
  return caseItem.topic || caseItem.title || 'Untitled Case';
};

// Helper to format category for display
const formatCategory = (category: string, language: string) => {
  const categoryMap: Record<string, { en: string; nb: string }> = {
    'arbeidsrett': { en: 'Employment Law', nb: 'Arbeidsrett' },
    'employment': { en: 'Employment Law', nb: 'Arbeidsrett' },
    'familierett': { en: 'Family Law', nb: 'Familierett' },
    'family': { en: 'Family Law', nb: 'Familierett' },
    'kontraktsrett': { en: 'Contract Law', nb: 'Kontraktsrett' },
    'contract': { en: 'Contract Law', nb: 'Kontraktsrett' },
    'eiendom': { en: 'Real Estate', nb: 'Eiendom' },
    'real_estate': { en: 'Real Estate', nb: 'Eiendom' },
    'corporate': { en: 'Corporate', nb: 'Selskapsrett' },
    'selskapsrett': { en: 'Corporate', nb: 'Selskapsrett' },
    'maritime': { en: 'Maritime', nb: 'Sjørett' },
    'intellectual_property': { en: 'IP Law', nb: 'Immaterialrett' },
    'tax': { en: 'Tax', nb: 'Skatt' },
    'criminal': { en: 'Criminal', nb: 'Strafferett' },
    'immigration': { en: 'Immigration', nb: 'Utlendingsrett' },
  };
  
  const key = category?.toLowerCase();
  if (categoryMap[key]) {
    return language === 'en' ? categoryMap[key].en : categoryMap[key].nb;
  }
  // Capitalize first letter if not found
  return category ? category.charAt(0).toUpperCase() + category.slice(1) : null;
};

// Helper to format date
const formatDate = (dateStr: string, language: string) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return language === 'en' ? 'Today' : 'I dag';
    if (diffDays === 1) return language === 'en' ? 'Yesterday' : 'I går';
    if (diffDays < 7) return language === 'en' ? `${diffDays} days ago` : `${diffDays} dager siden`;
    
    return date.toLocaleDateString(language === 'en' ? 'en-GB' : 'nb-NO', {
      day: 'numeric',
      month: 'short'
    });
  } catch {
    return null;
  }
};

// Status badge colors
const getStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    'ASSIGNED': 'bg-blue-100 text-blue-700',
    'IN_PROGRESS': 'bg-yellow-100 text-yellow-700',
    'ACTIVE': 'bg-green-100 text-green-700',
    'COMPLETED': 'bg-gray-100 text-gray-600',
    'CLOSED': 'bg-gray-100 text-gray-500',
    'PENDING': 'bg-orange-100 text-orange-700',
    'NEW': 'bg-violet-100 text-violet-700',
  };
  return statusColors[status?.toUpperCase()] || 'bg-gray-100 text-gray-600';
};

const formatStatus = (status: string, language: string) => {
  const statusMap: Record<string, { en: string; nb: string }> = {
    'ASSIGNED': { en: 'Assigned', nb: 'Tildelt' },
    'IN_PROGRESS': { en: 'In Progress', nb: 'Pågår' },
    'ACTIVE': { en: 'Active', nb: 'Aktiv' },
    'COMPLETED': { en: 'Completed', nb: 'Fullført' },
    'CLOSED': { en: 'Closed', nb: 'Lukket' },
    'PENDING': { en: 'Pending', nb: 'Venter' },
    'NEW': { en: 'New', nb: 'Ny' },
  };
  const key = status?.toUpperCase();
  if (statusMap[key]) {
    return language === 'en' ? statusMap[key].en : statusMap[key].nb;
  }
  return status || '';
};

// Case Card Component - responsive
const CaseCard = ({ caseItem, onClick, language, isRecent = false }: {
  caseItem: any;
  onClick: () => void;
  language: string;
  isRecent?: boolean;
}) => {
  const title = getCaseTitle(caseItem);
  const clientName = caseItem.client_name || caseItem.client;
  const clientOrg = caseItem.client_org || caseItem.client_type === 'COMPANY';
  const category = formatCategory(caseItem.category || caseItem.case_type, language);
  const dateDisplay = formatDate(caseItem.created_at || caseItem.updated_at, language);
  const status = caseItem.status;
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 sm:p-4 hover:bg-gray-50 rounded-xl transition-all border border-transparent hover:border-gray-100 group"
    >
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Icon - smaller on mobile */}
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isRecent ? 'bg-violet-100' : 'bg-gray-100'
        } group-hover:scale-105 transition-transform`}>
          <Briefcase className={`w-4 h-4 sm:w-5 sm:h-5 ${isRecent ? 'text-violet-600' : 'text-gray-500'}`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="text-sm font-medium text-gray-900 truncate mb-0.5 sm:mb-1">
            {title}
          </div>
          
          {/* Meta row - responsive wrapping */}
          <div className="flex items-center gap-2 sm:gap-3 text-xs text-gray-500 flex-wrap">
            {/* Client */}
            {clientName && (
              <div className="flex items-center gap-1">
                {clientOrg ? (
                  <Building2 className="w-3 h-3" />
                ) : (
                  <User className="w-3 h-3" />
                )}
                <span className="truncate max-w-[80px] sm:max-w-[120px]">{clientName}</span>
              </div>
            )}
            
            {/* Category - hide on very small screens if too many items */}
            {category && (
              <div className="hidden xs:flex items-center gap-1">
                <Tag className="w-3 h-3" />
                <span>{category}</span>
              </div>
            )}
            
            {/* Date */}
            {dateDisplay && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{dateDisplay}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Status Badge - smaller on mobile */}
        {status && (
          <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-medium flex-shrink-0 ${getStatusColor(status)}`}>
            {formatStatus(status, language)}
          </span>
        )}
      </div>
    </button>
  );
};

const CaseSelectionView = ({
  cases,
  recentCases,
  filteredCases,
  searchQuery,
  onSearchChange,
  onSelectCase,
  onCreateNewCase
}: {
  cases: any[];
  recentCases: any[];
  filteredCases: any[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelectCase: (caseItem: any) => void;
  onCreateNewCase: () => void;
}) => {
  const { language } = useTranslation();
  
  // Translations
  const t = {
    selectCase: language === 'en' ? 'Select a case' : 'Velg en sak',
    subtitle: language === 'en' ? 'AI conversations are linked to cases for full traceability' : 'AI-samtaler knyttes til saker for full sporbarhet',
    searchPlaceholder: language === 'en' ? 'Search for case, client, or category...' : 'Søk etter sak, klient eller kategori...',
    recentlyUsed: language === 'en' ? 'Recently used' : 'Nylig brukt',
    searchResults: (count: number) => language === 'en' ? `Search results (${count})` : `Søkeresultater (${count})`,
    allCases: (count: number) => language === 'en' ? `All cases (${count})` : `Alle saker (${count})`,
    otherCases: (count: number) => language === 'en' ? `Other cases (${count})` : `Andre saker (${count})`,
    noCasesFound: language === 'en' ? 'No cases found' : 'Ingen saker funnet',
    noCasesAvailable: language === 'en' ? 'No cases available' : 'Ingen saker tilgjengelig',
    noOtherCases: language === 'en' ? 'No other cases' : 'Ingen andre saker',
    createNewCase: language === 'en' ? 'Create new case' : 'Opprett ny sak',
    tryDifferentSearch: language === 'en' ? 'Try a different search term' : 'Prøv et annet søkeord'
  };

  const showRecentSection = !searchQuery && recentCases.length > 0;
  // Always show all cases section (either search results or all cases)
  const showAllCasesSection = true;
  
  // Filter out recent cases from the all cases list to avoid duplicates
  const recentCaseIds = new Set(recentCases.map(c => c.id));
  const nonRecentCases = cases.filter(c => !recentCaseIds.has(c.id));
  const casesToDisplay = searchQuery ? filteredCases : (showRecentSection ? nonRecentCases : cases);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      <div className="flex-1 flex items-start sm:items-center justify-center overflow-y-auto overflow-x-hidden px-3 sm:px-4 md:px-6">
        <div className="w-full max-w-xl lg:max-w-2xl py-4 sm:py-6 md:py-8">
          {/* Header - responsive */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-1 sm:mb-2">
              {t.selectCase}
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 px-2">
              {t.subtitle}
            </p>
          </div>

          {/* Search - responsive */}
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pl-9 sm:pl-10 bg-gray-50 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:bg-white transition-all placeholder:text-gray-400"
              />
              <svg 
                className="w-4 h-4 text-gray-400 absolute left-3 sm:left-3.5 top-1/2 -translate-y-1/2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Recent Cases - responsive */}
          {showRecentSection && (
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center gap-2 mb-2 sm:mb-3 px-1">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t.recentlyUsed}
                </h3>
              </div>
              <div className="space-y-1 bg-gray-50/50 rounded-xl p-1.5 sm:p-2">
                {recentCases.slice(0, 3).map((caseItem) => (
                  <CaseCard
                    key={caseItem.id}
                    caseItem={caseItem}
                    onClick={() => onSelectCase(caseItem)}
                    language={language}
                    isRecent={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Cases / Search Results - responsive */}
          {showAllCasesSection && (
            <div className="mb-4 sm:mb-6">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 sm:mb-3 px-1">
                {searchQuery 
                  ? t.searchResults(filteredCases.length) 
                  : (showRecentSection ? t.otherCases(casesToDisplay.length) : t.allCases(casesToDisplay.length))}
              </h3>
              <div className="max-h-[300px] sm:max-h-[400px] overflow-y-auto rounded-xl">
                {casesToDisplay.length === 0 ? (
                  <div className="px-3 sm:px-4 py-6 sm:py-8 text-center bg-gray-50 rounded-xl">
                    <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      {searchQuery ? t.noCasesFound : (showRecentSection ? t.noOtherCases : t.noCasesAvailable)}
                    </p>
                    {searchQuery && (
                      <p className="text-xs text-gray-400 mt-1">{t.tryDifferentSearch}</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {casesToDisplay.map((caseItem) => (
                      <CaseCard
                        key={caseItem.id}
                        caseItem={caseItem}
                        onClick={() => onSelectCase(caseItem)}
                        language={language}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create New Case Button - responsive */}
          <button
            onClick={onCreateNewCase}
            className="w-full px-3 sm:px-4 py-3 sm:py-3.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-all hover:shadow-lg flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{t.createNewCase}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaseSelectionView;
